import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import VBox from '../../components/layout/VBox';
import HBox from '../../components/layout/HBox';
import Grid, { GridItem } from '../../components/layout/Grid';
import Botao from '../../components/Botao';
import { mdiContentSave, mdiLoading, mdiEmail, mdiContentCopy, mdiOpenInNew } from '@mdi/js';
import Icon from '@/components/Icon';
import styles from './SecaoPreferenciasEmail.module.css';

interface PreferenciasEmail {
    [categoria: string]: string;
}

interface ConfiguracaoEmailItem {
    email: string;
    preferencias: PreferenciasEmail;
}

interface ConfiguracoesInscricao {
    por_email: ConfiguracaoEmailItem[];
    padrao?: PreferenciasEmail;
}

interface CategoriaInfo {
    nome: string;
    descricao: string;
}

interface FrequenciaInfo {
    nome: string;
    descricao: string;
}

interface DadosOrganizacao {
    uuid: string;
    email?: string;
    email_administrativo?: string;
    configuracoes_inscricao?: ConfiguracoesInscricao;
}

const categoriasFinanceiras = ['FATURA', 'MARKETING'];
const categoriasAdministrativas = ['ADMINISTRATIVA', 'NOTIFICACAO_SISTEMA'];

const categoriasInfo: Record<string, CategoriaInfo> = {
    FATURA: {
        nome: 'Faturas e Cobranças',
        descricao: 'Notificações sobre faturas, pagamentos e cobranças',
    },
    ADMINISTRATIVA: {
        nome: 'Mensagens Administrativas',
        descricao: 'Comunicados importantes sobre sua conta e serviços',
    },
    MARKETING: {
        nome: 'Marketing e Novidades',
        descricao: 'Novos recursos, promoções e conteúdo relevante',
    },
    NOTIFICACAO_SISTEMA: {
        nome: 'Notificações do Sistema',
        descricao: 'Alertas técnicos e notificações de segurança',
    },
};

const frequenciasInfo: Record<string, FrequenciaInfo> = {
    TODAS: {
        nome: 'Todas',
        descricao: 'Receber todas as notificações',
    },
    IMPORTANTES: {
        nome: 'Importantes',
        descricao: 'Apenas notificações críticas',
    },
    NENHUMA: {
        nome: 'Nenhuma',
        descricao: 'Não receber notificações',
    },
};

const SecaoPreferenciasEmail = () => {
    const queryClient = useQueryClient();

    // Query para buscar dados da organização
    const { data: organizacao, isLoading } = useQuery<DadosOrganizacao>(
        'organizacao/detalhes',
        async () => {
            const response = await axios.get('/api/organizacao/detalhes', {
                withCredentials: true,
            });
            return response.data.result;
        },
        {
            refetchOnWindowFocus: false,
        }
    );

    // Mutation para salvar preferências
    const saveMutation = useMutation(
        async (data: { configuracoes_inscricao: ConfiguracoesInscricao }) => {
            return await axios.put('/api/organizacao/detalhes', data, {
                withCredentials: true,
            });
        },
        {
            onMutate: async (newData) => {
                // Cancel any outgoing refetches
                await queryClient.cancelQueries('organizacao/detalhes');

                // Snapshot the previous value
                const previousData = queryClient.getQueryData('organizacao/detalhes');

                // Optimistically update to the new value
                queryClient.setQueryData('organizacao/detalhes', (old: any) => ({
                    ...old,
                    configuracoes_inscricao: newData.configuracoes_inscricao,
                }));

                // Return a context object with the snapshotted value
                return { previousData };
            },
            onSuccess: (response) => {
                // Atualizar cache com os dados retornados pelo servidor
                if (response.data?.result) {
                    queryClient.setQueryData('organizacao/detalhes', response.data.result);
                }

                toast('Preferências de email salvas com sucesso', {
                    position: 'top-center',
                    type: 'success',
                    autoClose: 5000,
                });
            },
            onError: (error: any, newData, context: any) => {
                // If the mutation fails, use the context returned from onMutate to roll back
                if (context?.previousData) {
                    queryClient.setQueryData('organizacao/detalhes', context.previousData);
                }
                console.error(error);
                const message = error.response?.data?.msg || 'Erro ao salvar preferências';
                toast(message, {
                    position: 'top-center',
                    type: 'error',
                    autoClose: 5000,
                });
            },
        }
    );

    const getEmailsFinanceiros = (): string[] => {
        if (!organizacao?.email) return [];
        return organizacao.email.split(',').map((e) => e.trim()).filter((e) => e);
    };

    const getEmailsAdministrativos = (): string[] => {
        if (!organizacao?.email_administrativo) return [];
        return organizacao.email_administrativo.split(',').map((e) => e.trim()).filter((e) => e);
    };

    const getPreferenciasEmail = (email: string): PreferenciasEmail => {
        const configs = organizacao?.configuracoes_inscricao?.por_email || [];
        const config = configs.find((c) => c.email.toLowerCase() === email.toLowerCase());
        return config?.preferencias || organizacao?.configuracoes_inscricao?.padrao || {};
    };

    const handleChangePreferencia = (email: string, categoria: string, frequencia: string) => {
        if (!organizacao) return;

        const configs = organizacao.configuracoes_inscricao?.por_email || [];
        const configIndex = configs.findIndex((c) => c.email.toLowerCase() === email.toLowerCase());

        let novasConfigs: ConfiguracaoEmailItem[];

        if (configIndex >= 0) {
            // Atualizar existente
            novasConfigs = [...configs];
            novasConfigs[configIndex] = {
                ...novasConfigs[configIndex],
                preferencias: {
                    ...novasConfigs[configIndex].preferencias,
                    [categoria]: frequencia,
                },
            };
        } else {
            // Criar nova
            novasConfigs = [
                ...configs,
                {
                    email,
                    preferencias: { [categoria]: frequencia },
                },
            ];
        }

        const novasConfiguracoesInscricao: ConfiguracoesInscricao = {
            ...organizacao.configuracoes_inscricao,
            por_email: novasConfigs,
        };

        saveMutation.mutate({
            configuracoes_inscricao: novasConfiguracoesInscricao,
        });
    };

    const copiarLinkConfiguracao = (email: string) => {
        const link = `${window.location.origin}/inscricao/org/${organizacao?.uuid}/config?email=${encodeURIComponent(email)}`;
        navigator.clipboard.writeText(link);
        toast('Link copiado para a área de transferência!', {
            position: 'top-center',
            type: 'success',
            autoClose: 2000,
        });
    };

    const abrirLinkConfiguracao = (email: string) => {
        const link = `/inscricao/org/${organizacao?.uuid}/config?email=${encodeURIComponent(email)}`;
        window.open(link, '_blank');
    };

    if (isLoading) {
        return (
            <VBox center style={{ padding: '40px' }}>
                <Botao variant="outline-secondary" icon={mdiLoading} disabled>
                    Carregando...
                </Botao>
            </VBox>
        );
    }

    const emailsFinanceiros = getEmailsFinanceiros();
    const emailsAdministrativos = getEmailsAdministrativos();

    return (
        <VBox gap="24px">
            <HBox>
                <VBox gap="8px">
                    <h3>Preferências de Email</h3>
                    <p style={{ color: 'var(--tc-color-gray-600)' }}>
                        Configure as preferências de notificação por email para cada destinatário.
                        Cada pessoa pode também ajustar suas próprias preferências através do link público.
                    </p>
                </VBox>
            </HBox>

            {/* Emails Financeiros */}
            {emailsFinanceiros.length > 0 && (
                <VBox gap="16px">
                    <h4>📧 Emails Financeiros</h4>
                    <p style={{ color: 'var(--tc-color-gray-600)', fontSize: '14px' }}>
                        Estes emails recebem faturas e comunicações de marketing.
                    </p>

                    {emailsFinanceiros.map((email) => (
                        <div key={email} className={styles.emailCard}>
                            <HBox style={{ marginBottom: '16px', alignItems: 'center' }}>
                                <Icon path={mdiEmail} size={0.8} />
                                <strong style={{ flex: 1 }}>{email}</strong>
                            </HBox>

                            <Grid>
                                {categoriasFinanceiras.map((categoria) => {
                                    const preferencias = getPreferenciasEmail(email);
                                    const frequenciaAtual = preferencias[categoria] || 'TODAS';

                                    return (
                                        <GridItem key={categoria} sm={12} md={6}>
                                            <div className={styles.categoriaBox}>
                                                <h5>{categoriasInfo[categoria].nome}</h5>
                                                <p className={styles.categoriaDesc}>
                                                    {categoriasInfo[categoria].descricao}
                                                </p>
                                                <div className={styles.radioGroup}>
                                                    {Object.keys(frequenciasInfo).map((freq) => (
                                                        <label key={freq} className={styles.radioLabel}>
                                                            <input
                                                                type="radio"
                                                                name={`${email}-${categoria}`}
                                                                value={freq}
                                                                checked={frequenciaAtual === freq}
                                                                onChange={(e) =>
                                                                    handleChangePreferencia(
                                                                        email,
                                                                        categoria,
                                                                        e.target.value
                                                                    )
                                                                }
                                                                disabled={saveMutation.isLoading}
                                                            />
                                                            <span title={frequenciasInfo[freq].descricao}>
                                                                {frequenciasInfo[freq].nome}
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </GridItem>
                                    );
                                })}
                            </Grid>
                        </div>
                    ))}
                </VBox>
            )}

            {/* Emails Administrativos */}
            {emailsAdministrativos.length > 0 && (
                <VBox gap="16px">
                    <h4>🔧 Emails Administrativos</h4>
                    <p style={{ color: 'var(--tc-color-gray-600)', fontSize: '14px' }}>
                        Estes emails recebem mensagens administrativas e notificações do sistema.
                    </p>

                    {emailsAdministrativos.map((email) => (
                        <div key={email} className={styles.emailCard}>
                            <HBox style={{ marginBottom: '16px', alignItems: 'center' }}>
                                <Icon path={mdiEmail} size={0.8} />
                                <strong style={{ flex: 1 }}>{email}</strong>
                            </HBox>

                            <Grid>
                                {categoriasAdministrativas.map((categoria) => {
                                    const preferencias = getPreferenciasEmail(email);
                                    const frequenciaAtual = preferencias[categoria] || 'TODAS';

                                    return (
                                        <GridItem key={categoria} sm={12} md={6}>
                                            <div className={styles.categoriaBox}>
                                                <h5>{categoriasInfo[categoria].nome}</h5>
                                                <p className={styles.categoriaDesc}>
                                                    {categoriasInfo[categoria].descricao}
                                                </p>
                                                <div className={styles.radioGroup}>
                                                    {Object.keys(frequenciasInfo).map((freq) => (
                                                        <label key={freq} className={styles.radioLabel}>
                                                            <input
                                                                type="radio"
                                                                name={`${email}-${categoria}`}
                                                                value={freq}
                                                                checked={frequenciaAtual === freq}
                                                                onChange={(e) =>
                                                                    handleChangePreferencia(
                                                                        email,
                                                                        categoria,
                                                                        e.target.value
                                                                    )
                                                                }
                                                                disabled={saveMutation.isLoading}
                                                            />
                                                            <span title={frequenciasInfo[freq].descricao}>
                                                                {frequenciasInfo[freq].nome}
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </GridItem>
                                    );
                                })}
                            </Grid>
                        </div>
                    ))}
                </VBox>
            )}

            {emailsFinanceiros.length === 0 && emailsAdministrativos.length === 0 && (
                <div className={styles.emptyState}>
                    <Icon path={mdiEmail} size={2} color="var(--tc-color-gray-400)" />
                    <p>Nenhum email cadastrado na organização.</p>
                    <p style={{ fontSize: '14px', color: 'var(--tc-color-gray-600)' }}>
                        Configure os emails da organização na seção <strong>Organização</strong>.
                    </p>
                </div>
            )}
        </VBox>
    );
};

export default SecaoPreferenciasEmail;
